import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Campaign, Agent, CampaignCall, fetchCampaigns, fetchExotelAgents, uploadCampaign, updateCampaign, startCampaign, pauseCampaign, resumeCampaign, restartCampaign, fetchCampaignCalls } from '../utils/api';
import './CSVCampaigns.css';

interface CSVCampaignsProps {
  userRole: 'super_admin' | 'admin' | 'customer';
}

const CSVCampaigns: React.FC<CSVCampaignsProps> = ({ userRole }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [campaignAgentId, setCampaignAgentId] = useState('');
  const [concurrency, setConcurrency] = useState<number>(3);
  const [contactsInput, setContactsInput] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  // Selected campaign call records list
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignCalls, setCampaignCalls] = useState<CampaignCall[]>([]);
  const [callsLoading, setCallsLoading] = useState(false);

  // Tracks which campaign ID currently has a pause/resume/restart action in-flight,
  // so we can disable its buttons and avoid double-clicks.
  const [actionInProgressId, setActionInProgressId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  // Modal states
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  // Edit form states
  const [editCampaignName, setEditCampaignName] = useState('');
  const [editCampaignAgentId, setEditCampaignAgentId] = useState('');
  const [editConcurrency, setEditConcurrency] = useState<number>(3);
  const [editContactsInput, setEditContactsInput] = useState('');
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [editingInProgress, setEditingInProgress] = useState(false);


  const loadCampaignData = async () => {
    setLoading(true);
    try {
      const camps = await fetchCampaigns();
      setCampaigns(camps);
      const agentList = await fetchExotelAgents();
      setAgents(agentList);
    } catch (err) {
      console.error('Failed to load campaigns/agents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaignData();
  }, []);

  /** Parse an Excel file (ArrayBuffer) into CSV-like text using sheet_to_csv */
  const parseExcelToCsv = (buffer: ArrayBuffer): string => {
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_csv(sheet);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const buffer = event.target?.result as ArrayBuffer;
        const csv = parseExcelToCsv(buffer);
        setContactsInput(csv);
      };
      reader.readAsArrayBuffer(file);
    } else {
      // CSV / TXT
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        setContactsInput(text);
      };
      reader.readAsText(file);
    }
  };

  const parseContacts = (rawText: string): { phone: string; name?: string; variables?: Record<string, string> }[] => {
    const list: { phone: string; name?: string; variables?: Record<string, string> }[] = [];
    const lines = rawText.split('\n').map(line => line.trim()).filter(Boolean);
    if (lines.length === 0) return list;

    // Detect if first line is a header row
    const firstLine = lines[0];
    const firstCols = firstLine.split(',').map(c => c.trim().toLowerCase());
    
    // We assume it has a header if any column contains 'phone', 'mobile', 'name', 'number', or if there are non-numeric strings in the columns.
    const hasHeader = firstCols.includes('phone') || 
                      firstCols.includes('mobile') || 
                      firstCols.includes('number') || 
                      firstCols.includes('name') ||
                      isNaN(Number(firstCols[0].replace(/[+\-\s()]/g, '')));

    let headers: string[] = [];
    let startIdx = 0;
    let phoneIdx = 0;
    let nameIdx = 1;

    if (hasHeader) {
      headers = firstLine.split(',').map(c => c.trim());
      startIdx = 1;
      
      const lowerHeaders = headers.map(h => h.toLowerCase());
      const pIdx = lowerHeaders.findIndex(h => h.includes('phone') || h.includes('mobile') || h.includes('number'));
      if (pIdx !== -1) phoneIdx = pIdx;
      
      const nIdx = lowerHeaders.findIndex(h => h.includes('name'));
      if (nIdx !== -1) nameIdx = nIdx;
    }

    for (let i = startIdx; i < lines.length; i++) {
      const parts = lines[i].split(',').map(c => c.trim());
      const phone = parts[phoneIdx];
      if (!phone) continue;

      const name = nameIdx < parts.length ? parts[nameIdx] : undefined;
      const variables: Record<string, string> = {};

      if (hasHeader) {
        headers.forEach((header, colIdx) => {
          if (colIdx !== phoneIdx && colIdx !== nameIdx && colIdx < parts.length) {
            variables[header] = parts[colIdx];
          }
        });
      } else {
        parts.forEach((part, colIdx) => {
          if (colIdx !== phoneIdx && colIdx !== nameIdx) {
            variables[`var${colIdx + 1}`] = part;
          }
        });
      }

      list.push({
        phone,
        name: name || undefined,
        variables: Object.keys(variables).length > 0 ? variables : undefined
      });
    }
    return list;
  };

  const downloadSampleCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "phone,name,balance,date\n"
      + "+919999999999,John Doe,Rs. 5000,2026-06-20\n"
      + "+918888888888,Jane Smith,Rs. 12000,2026-06-25";
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sample_campaign.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSampleExcel = () => {
    const rows = [
      ['phone', 'name', 'balance', 'date'],
      ['+919999999999', 'John Doe', 'Rs. 5000', '2026-06-20'],
      ['+918888888888', 'Jane Smith', 'Rs. 12000', '2026-06-25'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
    XLSX.writeFile(wb, 'sample_campaign.xlsx');
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError('');
    setUploadSuccess('');

    if (!campaignName.trim()) {
      setUploadError('Campaign Name is required');
      return;
    }
    if (!campaignAgentId) {
      setUploadError('Please select an Agent');
      return;
    }
    if (!contactsInput.trim()) {
      setUploadError('Please paste contacts or select a CSV / Excel file');
      return;
    }

    const contacts = parseContacts(contactsInput);
    if (contacts.length === 0) {
      setUploadError('No valid contacts found in input (Format: phone,name or one phone number per line)');
      return;
    }

    setUploading(true);
    try {
      await uploadCampaign(campaignName.trim(), campaignAgentId, contacts, concurrency);
      setUploadSuccess(`✓ Campaign "${campaignName}" created with ${contacts.length} contacts!`);
      setCampaignName('');
      setConcurrency(1);
      setContactsInput('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadCampaignData();
    } catch (err: any) {
      setUploadError(err.message || 'Failed to create campaign');
    } finally {
      setUploading(false);
    }
  };

  const handleStartCampaign = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await startCampaign(id);
      alert('Campaign started successfully!');
      await loadCampaignData();
      if (selectedCampaign?.id === id) {
        openCampaignDetails(id);
      }
    } catch (err: any) {
      alert(`Error starting campaign: ${err.message}`);
    }
  };

  const handlePauseCampaign = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActionInProgressId(id);
    try {
      await pauseCampaign(id);
      await loadCampaignData();
      if (selectedCampaign?.id === id) {
        openCampaignDetails(id);
      }
    } catch (err: any) {
      alert(`Error pausing campaign: ${err.message}`);
    } finally {
      setActionInProgressId(null);
    }
  };

  const handleResumeCampaign = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActionInProgressId(id);
    try {
      await resumeCampaign(id);
      await loadCampaignData();
      if (selectedCampaign?.id === id) {
        openCampaignDetails(id);
      }
    } catch (err: any) {
      alert(`Error resuming campaign: ${err.message}`);
    } finally {
      setActionInProgressId(null);
    }
  };

  const handleRestartCampaign = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      'Restarting will reset ALL contacts in this campaign back to pending and re-dial from contact #1, including ones already completed. Continue?'
    );
    if (!confirmed) return;

    setActionInProgressId(id);
    try {
      await restartCampaign(id);
      await loadCampaignData();
      if (selectedCampaign?.id === id) {
        openCampaignDetails(id);
      }
    } catch (err: any) {
      alert(`Error restarting campaign: ${err.message}`);
    } finally {
      setActionInProgressId(null);
    }
  };

  const openCampaignDetails = async (id: string) => {
    const campaign = campaigns.find((c) => c.id === id) || null;
    setSelectedCampaign(campaign);
    if (!campaign) return;

    setCallsLoading(true);
    try {
      const calls = await fetchCampaignCalls(id);
      setCampaignCalls(calls);
    } catch (err) {
      console.error('Failed to load campaign calls:', err);
    } finally {
      setCallsLoading(false);
    }
  };

  const formatCallsToCSV = (calls: CampaignCall[]): string => {
    if (calls.length === 0) return '';
    
    // Find unique variable keys
    const varKeysSet = new Set<string>();
    const callsWithParsedVars = calls.map(call => {
      let variables: Record<string, string> = {};
      if (call.variables) {
        try {
          variables = typeof call.variables === 'string' ? JSON.parse(call.variables) : call.variables;
          Object.keys(variables).forEach(k => varKeysSet.add(k));
        } catch (e) {
          console.error('Error parsing variables:', e);
        }
      }
      return { ...call, parsedVars: variables };
    });

    const varKeys = Array.from(varKeysSet);
    const headers = ['phone', 'name', ...varKeys];
    
    const lines = [
      headers.join(','),
      ...callsWithParsedVars.map(call => {
        const row = [
          call.phone,
          call.name || '',
          ...varKeys.map(k => call.parsedVars[k] || '')
        ];
        return row.join(',');
      })
    ];

    return lines.join('\n');
  };

  const openEditModal = async (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setEditCampaignName(campaign.name);
    setEditCampaignAgentId(campaign.agentId);
    setEditConcurrency(campaign.concurrency || 1);
    setEditError('');
    setEditSuccess('');
    
    setLoading(true);
    try {
      const calls = await fetchCampaignCalls(campaign.id);
      const csvText = formatCallsToCSV(calls);
      setEditContactsInput(csvText);
      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Failed to load campaign calls for editing:', err);
      alert('Failed to load campaign contacts for editing.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const buffer = event.target?.result as ArrayBuffer;
        const csv = parseExcelToCsv(buffer);
        setEditContactsInput(csv);
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        setEditContactsInput(text);
      };
      reader.readAsText(file);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCampaign) return;
    
    setEditError('');
    setEditSuccess('');

    if (!editCampaignName.trim()) {
      setEditError('Campaign Name is required');
      return;
    }
    if (!editCampaignAgentId) {
      setEditError('Please select an Agent');
      return;
    }
    if (!editContactsInput.trim()) {
      setEditError('Please paste contacts or select a CSV file');
      return;
    }

    const contacts = parseContacts(editContactsInput);
    if (contacts.length === 0) {
      setEditError('No valid contacts found in input');
      return;
    }

    setEditingInProgress(true);
    try {
      await updateCampaign(editingCampaign.id, editCampaignName.trim(), editCampaignAgentId, contacts, editConcurrency);
      setEditSuccess('✓ Campaign updated successfully!');
      
      await loadCampaignData();
      
      if (selectedCampaign?.id === editingCampaign.id) {
        await openCampaignDetails(editingCampaign.id);
      }
      
      setTimeout(() => {
        setIsEditModalOpen(false);
        setEditingCampaign(null);
      }, 800);
    } catch (err: any) {
      setEditError(err.message || 'Failed to update campaign');
    } finally {
      setEditingInProgress(false);
    }
  };

  // Renders the appropriate action button(s) for a campaign's current status.
  // Restart is intentionally hidden while a campaign is actively 'calling'
  // to avoid a double-trigger race with the in-progress call chain.
  const renderCampaignActions = (c: Campaign, compact: boolean) => {
    const busy = actionInProgressId === c.id;
    const btnStyle = compact ? { margin: 0 } : undefined;

    return (
      <>
        {c.status === 'pending' && (
          <button
            className="run-campaign-action-btn"
            style={btnStyle}
            disabled={busy}
            onClick={(e) => handleStartCampaign(c.id, e)}
          >
            ▶ Start
          </button>
        )}
        {c.status === 'calling' && (
          <button
            className="run-campaign-action-btn pause"
            style={btnStyle}
            disabled={busy}
            onClick={(e) => handlePauseCampaign(c.id, e)}
          >
            ⏸ Pause
          </button>
        )}
        {c.status === 'paused' && (
          <button
            className="run-campaign-action-btn resume"
            style={btnStyle}
            disabled={busy}
            onClick={(e) => handleResumeCampaign(c.id, e)}
          >
            ▶ Resume
          </button>
        )}
        {c.status !== 'calling' && c.status !== 'pending' && (
          <button
            className="run-campaign-action-btn restart"
            style={btnStyle}
            disabled={busy}
            onClick={(e) => handleRestartCampaign(c.id, e)}
          >
            ↻ Restart
          </button>
        )}
      </>
    );
  };

  return (
    <div className="campaigns-view-container">
      {/* Left: Upload and List Section */}
      <div className="campaigns-left-panel">
        {/* Upload form */}
        <form onSubmit={handleCreateCampaign} className="campaign-upload-form-card">
          <h4>🚀 Upload Outbound Campaign</h4>
          
          <div className="form-row-custom">
            <div className="form-group-custom half">
              <label>Campaign Name *</label>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. Feedback Campaign"
                className="form-input-field"
              />
            </div>
            
            <div className="form-group-custom half">
              <label>Select Voice Agent *</label>
              <select
                value={campaignAgentId}
                onChange={(e) => setCampaignAgentId(e.target.value)}
                className="form-select-field"
              >
                <option value="">— Choose Agent —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group-custom half">
              <label>Concurrency Limit *</label>
              <input
                type="number"
                min="1"
                max="10"
                value={concurrency}
                onChange={(e) => setConcurrency(Math.max(1, parseInt(e.target.value) || 1))}
                className="form-input-field"
              />
            </div>
          </div>

          <div className="form-group-custom">
            <label>Paste Contacts (Format: <code>phone,name,balance,date,...</code> with headers, or <code>phone,name</code> per line)</label>
            <textarea
              value={contactsInput}
              onChange={(e) => setContactsInput(e.target.value)}
              placeholder="phone,name,balance,date&#10;+918600432414,John Doe,Rs. 5000,2026-06-20&#10;+918600432414,Jane Smith,Rs. 12000,2026-06-25"
              className="form-textarea-field short"
            />
          </div>

          {contactsInput.trim() && (
            <div className="file-parsed-badge">
              ✅ {parseContacts(contactsInput).length} contacts ready
            </div>
          )}

          <div className="csv-upload-row">
            <div className="file-input-group">
              <label>Upload CSV or Excel file:</label>
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv,.txt,.xlsx,.xls"
                onChange={handleFileUpload}
                className="form-file-input"
              />
              <span className="file-format-hint">Supports .csv, .xlsx, .xls</span>
            </div>
            <div className="csv-actions-group">
              <button type="button" className="download-sample-btn" onClick={downloadSampleCSV}>
                📥 Sample CSV
              </button>
              <button type="button" className="download-sample-btn excel" onClick={downloadSampleExcel}>
                📊 Sample Excel
              </button>
              <button type="submit" className="create-campaign-submit-btn" disabled={uploading}>
                {uploading ? 'Processing...' : 'Create Campaign'}
              </button>
            </div>
          </div>

          {uploadError && <div className="form-error-alert-label">{uploadError}</div>}
          {uploadSuccess && <div className="form-success-alert-label">{uploadSuccess}</div>}
        </form>

        {/* Campaigns List */}
        <div className="active-campaigns-list-card">
          <div className="card-header-row">
            <h4>Active Campaigns ({campaigns.length})</h4>
            <button
              type="button"
              className="expand-modal-btn"
              onClick={() => setIsListModalOpen(true)}
            >
              📱 View All in Modal
            </button>
          </div>
          {loading ? (
            <div className="panel-empty-state">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div className="panel-empty-state">No campaigns created yet.</div>
          ) : (
            <ul className="campaign-list-group">
              {campaigns.map((c) => {
                const total = c.totalCalls || 1;
                const completed = c.completedCalls + c.failedCalls;
                return (
                  <li
                    key={c.id}
                    className={`campaign-list-item-row${selectedCampaign?.id === c.id ? ' active' : ''}`}
                    onClick={() => openCampaignDetails(c.id)}
                  >
                    <div className="campaign-list-item-layout">
                      <div className="campaign-item-main-content">
                        <strong>{c.name}</strong>
                        <span className="campaign-meta-text">
                          Agent: {c.agentName || 'N/A'} · Lines: {c.concurrency || 1} · Contacts: {c.totalCalls}
                        </span>
                      </div>
                      <div className="campaign-item-right-content">
                        <span className={`status-pill ${c.status}`}>{c.status}</span>
                        <span className="campaign-progress-text">
                          Progress: {completed}/{c.totalCalls}
                        </span>
                        <div className="campaign-actions-inline">
                          {renderCampaignActions(c, false)}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Right: Calls list details */}
      <div className="campaigns-right-panel">
        {selectedCampaign ? (
          <div className="campaign-details-container">
            <div className="panel-title-bar-row">
              <h4>Campaign Dials: {selectedCampaign.name}</h4>
              <div className="detail-actions-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {selectedCampaign.status === 'pending' && (
                  <button
                    className="modal-edit-btn"
                    onClick={() => openEditModal(selectedCampaign)}
                  >
                    ✏️ Edit Campaign
                  </button>
                )}
                {renderCampaignActions(selectedCampaign, true)}
                <button
                  className="refresh-calls-btn"
                  onClick={() => openCampaignDetails(selectedCampaign.id)}
                >
                  ↻ Refresh Status
                </button>
              </div>
            </div>

            <div className="campaign-stats-badges-strip">
              <div className="stat-badge-box">
                <span>Total Calls</span>
                <strong>{selectedCampaign.totalCalls}</strong>
              </div>
              <div className="stat-badge-box">
                <span>Completed</span>
                <strong className="text-success">{selectedCampaign.completedCalls}</strong>
              </div>
              <div className="stat-badge-box">
                <span>Failed/Busy</span>
                <strong className="text-danger">{selectedCampaign.failedCalls}</strong>
              </div>
              <div className="stat-badge-box">
                <span>Status</span>
                <strong className={`status-label ${selectedCampaign.status}`}>{selectedCampaign.status}</strong>
              </div>
            </div>

            {callsLoading ? (
              <div className="panel-empty-state">Loading dial details...</div>
            ) : (
              <div className="campaign-calls-table-holder">
                <table className="campaign-calls-table">
                  <thead>
                    <tr>
                      <th>Contact Name</th>
                      <th>Phone Number</th>
                      <th>Dial Status</th>
                      <th>Duration</th>
                      <th>Call Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignCalls.map((call) => (
                      <tr key={call.id} className="call-record-row">
                        <td>{call.name || <span className="unnamed-label">—</span>}</td>
                        <td className="phone-value">{call.phone}</td>
                        <td>
                          <span className={`call-status-pill ${call.status}`}>{call.status}</span>
                        </td>
                        <td>{call.durationSeconds ? `${call.durationSeconds}s` : '—'}</td>
                        <td className="summary-field-ellipsis" title={call.summary || ''}>
                          {call.summary || '—'}
                        </td>
                      </tr>
                    ))}
                    {campaignCalls.length === 0 && (
                      <tr>
                        <td colSpan={5} className="no-calls-data">
                          No calls dialed yet. Click Start Campaign to trigger calls.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="form-empty-state-card">
            <span className="empty-state-icon">🚀</span>
            <p>Select a campaign from the list on the left to view active sequential dials and metrics.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      
      {/* 1. Campaigns List Modal */}
      {isListModalOpen && (
        <div className="campaign-modal-overlay" onClick={() => setIsListModalOpen(false)}>
          <div className="campaign-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="campaign-modal-header">
              <h3>📂 All Outbound Campaigns</h3>
              <button className="campaign-modal-close-btn" onClick={() => setIsListModalOpen(false)}>
                &times;
              </button>
            </div>
            <div className="campaign-modal-content">
              {campaigns.length === 0 ? (
                <div className="panel-empty-state">No campaigns created yet.</div>
              ) : (
                <div className="campaign-modal-table-holder">
                  <table className="campaign-modal-table">
                    <thead>
                      <tr>
                        <th>Campaign Name</th>
                        <th>Agent</th>
                        <th>Concurrency</th>
                        <th>Status</th>
                        <th>Progress</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((c) => (
                        <tr key={c.id}>
                          <td style={{ fontWeight: 600 }}>{c.name}</td>
                          <td>{c.agentName || 'N/A'}</td>
                          <td>{c.concurrency || 1} lines</td>
                          <td>
                            <span className={`status-pill ${c.status}`}>{c.status}</span>
                          </td>
                          <td>
                            {c.completedCalls + c.failedCalls} / {c.totalCalls}
                          </td>
                          <td>
                            <div className="modal-actions-cell">
                              <button
                                className="modal-select-btn"
                                onClick={() => {
                                  openCampaignDetails(c.id);
                                  setIsListModalOpen(false);
                                }}
                              >
                                Select
                              </button>
                              {renderCampaignActions(c, true)}
                              {c.status === 'pending' && (
                                <button
                                  className="modal-edit-btn"
                                  onClick={() => openEditModal(c)}
                                >
                                  ✏️ Edit
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="campaign-modal-footer">
              <button className="modal-cancel-btn" onClick={() => setIsListModalOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Edit Campaign Modal */}
      {isEditModalOpen && editingCampaign && (
        <div className="campaign-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div className="campaign-modal-container medium" onClick={(e) => e.stopPropagation()}>
            <div className="campaign-modal-header">
              <h3>✏️ Edit Campaign: {editingCampaign.name}</h3>
              <button className="campaign-modal-close-btn" onClick={() => setIsEditModalOpen(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="campaign-modal-content">
                {editError && <div className="form-error-alert-label" style={{ marginBottom: '16px' }}>{editError}</div>}
                {editSuccess && <div className="form-success-alert-label" style={{ marginBottom: '16px' }}>{editSuccess}</div>}

                <div className="form-row-custom">
                  <div className="form-group-custom half">
                    <label>Campaign Name *</label>
                    <input
                      type="text"
                      value={editCampaignName}
                      onChange={(e) => setEditCampaignName(e.target.value)}
                      placeholder="e.g. Feedback Campaign"
                      className="form-input-field"
                    />
                  </div>
                  
                  <div className="form-group-custom half">
                    <label>Select Voice Agent *</label>
                    <select
                      value={editCampaignAgentId}
                      onChange={(e) => setEditCampaignAgentId(e.target.value)}
                      className="form-select-field"
                    >
                      <option value="">— Choose Agent —</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-custom half">
                    <label>Concurrency Limit *</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={editConcurrency}
                      onChange={(e) => setEditConcurrency(Math.max(1, parseInt(e.target.value) || 1))}
                      className="form-input-field"
                    />
                  </div>
                </div>

                <div className="form-group-custom">
                  <label>Edit Contacts (Format: <code>phone,name,balance,date,...</code> with headers)</label>
                  <textarea
                    value={editContactsInput}
                    onChange={(e) => setEditContactsInput(e.target.value)}
                    placeholder="phone,name,balance,date&#10;+918600432414,John Doe,Rs. 5000,2026-06-20"
                    className="form-textarea-field"
                    style={{ height: '180px' }}
                  />
                </div>

                <div className="file-input-group">
                  <label>Or upload new CSV / Excel file:</label>
                  <input
                    type="file"
                    ref={editFileInputRef}
                    accept=".csv,.txt,.xlsx,.xls"
                    onChange={handleEditFileUpload}
                    className="form-file-input"
                  />
                  <span className="file-format-hint">Supports .csv, .xlsx, .xls</span>
                </div>
              </div>
              <div className="campaign-modal-footer">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={editingInProgress}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-save-btn"
                  disabled={editingInProgress}
                >
                  {editingInProgress ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSVCampaigns;